import { CycleBudgetField } from 'nomst/Stores/Configuration/cycleBudgetField';
import { PayRangeConfiguration } from 'nomst/Stores/Configuration/payRangeConfiguration';
import { RatingConfiguration } from 'nomst/Stores/Configuration/ratingConfiguration';
import { TeamPageViews } from 'nomst/Stores/Configuration/teamPageViews';
import { Format } from 'nomst/Stores/Utility/format';
import Coalesce from 'nomst/Stores/Utility/coalesce';
import { Currency } from 'nomst/Stores/Utility/currency';
import { IAmount } from 'typings/amount';
import {
    AmountCode, CycleStatus, PayRangeDisplayType, CompaRatioDisplayFormat, BaseOfCalculation,
    EmployeePayType, EmployeePayTypeTitle, PercentageCalculationRuleOptions, StockDisplayType, EmployeeNameFormat
} from 'typings/enums';
import { GetAllEmployeePayTypeTitles } from 'utils/employeePayTypeUtils';
import { action, observable, computed, makeObservable } from 'mobx';
import { ICycleBudget } from 'typings/cycleBudget';
import { formatDate, dateTextFormat } from 'utils/dateUtils';
import { WorkflowConfiguration } from 'nomst/Stores/Configuration/workflowConfiguration';
import { CycleBudgetFieldFactory } from 'nomst/Stores/Configuration/cycleBudgetFieldFactory';
import { EmployeeFormatConfiguration } from 'nomst/Stores/Configuration/employeeFormatConfiguration';

export class Configuration {
    private readonly _masterAccountId: string;
    @observable private readonly _cycleId: number;
    @observable private  _status: CycleStatus;
    @observable private readonly _homeCountryCurrency: string;
    @observable private readonly _currencies: Currency[];
    @observable private readonly _hasMultipleCurrency: boolean;
    @observable private readonly _employeeNameSortFormat: EmployeeNameFormat;
    @observable private readonly _cycleBudgetFieldsMap: Map<string, CycleBudgetField>;
    @observable private _cycleBudgets: ICycleBudget[];
    private readonly _format: Format;
    @observable private readonly _payRangeConfiguration: PayRangeConfiguration;
    @observable private readonly _ratingConfiguration: Nullable<RatingConfiguration>;
    @observable private readonly _workflowConfiguration: WorkflowConfiguration;
    @observable private readonly _teamPageViews: TeamPageViews;
    @observable private readonly _accessToken: string;
    @observable private readonly _userId: string;
    @observable private readonly _bonusFieldRules: IBonusFieldRule[];
    @observable private readonly _closedDate: Nullable<string>;
    @observable private _tccAmountConverter: {[key: string]: string};
    @observable private _startDate: string;
    @observable private _endDate: string;
    @observable private _payEffectiveDate: string;
    @observable private _comment: string;
    @observable private _previewMode: boolean;
    private _cycleBudgetFieldFactory: CycleBudgetFieldFactory;

    // tslint:disable:parameters-max-number
    constructor(
        masterAccountId: string,
        cycleId: number,
        status: string,
        homeCountryCurrency: string,
        currencies: Currency[],
        hasMultipleCurrency: boolean,
        employeeNameSortFormat: EmployeeFormatConfiguration,
        cycleBudgetFields: CycleBudgetField[],
        cycleBudgets: ICycleBudget[],
        format: Format,
        payRangeConfiguration: PayRangeConfiguration,
        ratingConfiguration: Nullable<RatingConfiguration>,
        workflowConfiguration: WorkflowConfiguration,
        teamPageViews: TeamPageViews,
        accessToken: string,
        userId: string,
        bonusFieldRules: IBonusFieldRule[],
        closedDate: Nullable<string>,
        endDate: string,
        startDate: string,
        payEffectiveDate: string,
        comment: string,
        previewMode: boolean) {
        makeObservable(this);
        this._masterAccountId = masterAccountId;
        this._cycleId = cycleId;
        this._status = status as CycleStatus;
        this._homeCountryCurrency = homeCountryCurrency;
        this._currencies = currencies;
        this._hasMultipleCurrency = hasMultipleCurrency;
        this._employeeNameSortFormat = employeeNameSortFormat.employeeNameSortFormat;
        this._cycleBudgetFieldsMap = new Map(cycleBudgetFields.map(cycleBudgetField =>
            [cycleBudgetField.amountCode, cycleBudgetField] as [string, CycleBudgetField]));

        this._cycleBudgets = Coalesce.array(cycleBudgets);
        this._format = format;
        this._payRangeConfiguration = payRangeConfiguration;
        this._ratingConfiguration = ratingConfiguration;
        this._workflowConfiguration = workflowConfiguration;
        this._teamPageViews = teamPageViews;
        this._accessToken = accessToken;
        this._userId = userId;
        this._bonusFieldRules = bonusFieldRules;
        this._closedDate = closedDate;
        this.createAmountConverter(cycleBudgetFields);
        this._endDate = endDate;
        this._startDate = startDate;
        this._payEffectiveDate = payEffectiveDate;
        this._comment = comment;
        this._previewMode = previewMode;
        this._cycleBudgetFieldFactory = new CycleBudgetFieldFactory();
    }

    @computed
    get masterAccountId(): string {
        return this._masterAccountId;
    }

    @computed
    get cycleId(): number {
        return this._cycleId;
    }

    @computed
    get isCurrentlyPending(): boolean {
        return this._status === CycleStatus.Pending;
    }

    @computed
    get isClosing(): boolean {
        return this._status === CycleStatus.Closing;
    }

    @computed
    get isClosed(): boolean {
        return this._status === CycleStatus.Closed;
    }

    @computed
    get isPublished(): boolean {
        return this._status === CycleStatus.Published;
    }

    @computed
    get isOpen(): boolean {
        return this._status === CycleStatus.Open;
    }

    @computed
    get isInPreviewMode(): boolean {
        return this._status === CycleStatus.Open && this._previewMode;
    }

    @computed
    get status(): CycleStatus {
        return this._status;
    }

    @computed
    get homeCountryCurrency(): string {
        return this._homeCountryCurrency;
    }

    @computed
    get currencies(): Currency[] {
        return this._currencies;
    }

    @computed
    get homeTargetCurrencies(): Currency[] {
        return this._currencies.filter(currency => currency.target === this._homeCountryCurrency);
    }

    @computed
    get hasMultipleCurrency(): boolean {
        return this._hasMultipleCurrency;
    }

    @computed
    get employeeNameSortFormat(): EmployeeNameFormat {
        return this._employeeNameSortFormat;
    }

    @computed
    get displayIncreaseRecommendation(): boolean {
        return this._teamPageViews.showIncreaseRecommendation;
    }

    @computed
    get isManagerIncreaseRestricted(): boolean {
        return this._teamPageViews.managerIncreaseDecision;
    }

    @computed
    get managerIncreasePercentageUnder(): number {
        return this._teamPageViews.managerIncreasePercentageUnder;
    }

    @computed
    get managerIncreasePercentageOver(): number {
        return this._teamPageViews.managerIncreasePercentageOver;
    }

    @computed
    get format(): Format {
        return this._format;
    }

    @computed
    get payRangeConfiguration(): PayRangeConfiguration {
        return this._payRangeConfiguration;
    }

    @computed
    get ratingConfiguration(): Nullable<RatingConfiguration> {
        return this._ratingConfiguration;
    }

    @computed
    get workflowConfiguration(): WorkflowConfiguration {
        return this._workflowConfiguration;
    }

    @computed
    get teamPageViews(): TeamPageViews {
        return this._teamPageViews;
    }

    @computed
    get accessToken(): string {
        return this._accessToken;
    }

    @computed
    get userId(): string {
        return this._userId;
    }

    @computed
    get bonusFieldRules(): IBonusFieldRule[] {
        return this._bonusFieldRules;
    }
    
    @computed
    get cycleBudgets(): ICycleBudget[] {
        return this._cycleBudgets;
    }

    @computed
    get closedDate(): Nullable<string> {
        return this._closedDate;
    }

    @computed
    get startDate(): string {
        return this._startDate;
    }

    @computed
    get endDate(): string {
        return this._endDate;
    }

    @computed
    get payEffectiveDate(): string {
        return this._payEffectiveDate;
    }

    @computed
    get comment(): string {
        return this._comment;
    }

    @computed
    get previewMode(): boolean {
        return this._previewMode;
    }

    @computed
    get useAutoCalculateLumpSum(): boolean {
        const cycleLumpSum = this.getCycleBudgetField(AmountCode.LumpSum);
        return cycleLumpSum
            && cycleLumpSum.rangeEnforcement
            && cycleLumpSum.rangeEnforcement.useAutoCalculateLumpSum;
    }

    @computed
    get configurationName(): string {
        return `${formatDate(this.payEffectiveDate, dateTextFormat)} Cycle`;
    }

    @action
    setStatus(status: CycleStatus): void {
        this._status = status;
    }

    @action
    setEndDate(value: string): void {
        this._endDate = value;
    }

    @action
    setPayEffectiveDate(value: string): void {
        this._payEffectiveDate = value;
    }

    @action
    setComment(value: string): void {
        this._comment = value;
    }

    @action
    addBudget(budget: ICycleBudget): void {
        this._cycleBudgets.push(budget);
    }

    @action
    removeBudget(budget: ICycleBudget): void {
        const cycleBudgets = this._cycleBudgets.filter(cycleBudget => cycleBudget.id !== budget.id);
        this._cycleBudgets = cycleBudgets;
    }

    @action
    createAmountConverter(budgets: CycleBudgetField[]): void {
        this._tccAmountConverter = {};
        budgets.forEach(budget => this._tccAmountConverter[budget.title] = budget.amountCode);
        this._tccAmountConverter[EmployeePayTypeTitle.Commissions] = EmployeePayType.Commissions;
        this._tccAmountConverter[EmployeePayTypeTitle.LastPaidSti] = EmployeePayType.LastPaidSti;
        this._tccAmountConverter[EmployeePayTypeTitle.OtherCash] = EmployeePayType.OtherCash;
    }

    getEligibilityRuleNote(amountCode: AmountCode): string {
        const cycleBudgetField = this._cycleBudgetFieldsMap.get(amountCode);
        if (cycleBudgetField) {
            const { eligibilityRule } = cycleBudgetField;
            return eligibilityRule && eligibilityRule.comment || '';
        }

        return '';
    }

    getPercentageRuleOptionValue(amountCode: string): string {
        const cycleBudgetField = this.getCycleBudgetField(amountCode);
        if (!cycleBudgetField) {
            return '';
        }

        const selectedOption = PercentageCalculationRuleOptions
            .find(option => option.value === cycleBudgetField.percentageCalculationRule);
        return selectedOption ? selectedOption.text : '';
    }

    getCycleBudgetField(amountCode: string): CycleBudgetField {
        return this._cycleBudgetFieldsMap.get(amountCode) || {} as CycleBudgetField;
    }

    getBudgetCycleFieldsTitle(cycleBudgetId: number): string[] {
        return this.cycleBudgetFields
            .filter(cycleBudgetField => cycleBudgetField.budgetCycleId === cycleBudgetId)
            .map(cycleBudgetField => cycleBudgetField.title);
    }

    addCycleBudgetField(code: AmountCode): void {
        if (this._cycleBudgetFieldsMap.has(code)) {
            return;
        }
        this._cycleBudgetFieldsMap.set(code,  this._cycleBudgetFieldFactory.getMetadata(code));
    }

    @action
    removeCycleBudgetField(code: AmountCode): void {
        this._cycleBudgetFieldsMap.delete(code);
    }

    getCycleBudgetColor(cycleBudgetId: number): string {
        const cycleBudget = this._cycleBudgets.find(budget =>
            budget.budgetCycleId === cycleBudgetId);

        return cycleBudget ? cycleBudget.color : '';
    }

    @computed
    get stockDisplayTypeOptionValue(): string {
        const stock = this.getCycleBudgetField(AmountCode.Stock);
        if (stock && stock.stockDisplayType) {
            return stock.stockDisplayType;
        }

        return StockDisplayType.Unit;
    }

    @action
    handleStockDisplayChange(value: string): void {
        const stock = this.getCycleBudgetField(AmountCode.Stock);
        if (!stock) {
            return;
        }

        stock.setStockDisplayType(value as StockDisplayType);
        if (stock.stockDisplayType === StockDisplayType.Unit) {
            stock.stockValue = null;
        }
    }

    @action
    handleAmountPercentageRuleChange(amountCode: string, value: string): void {
        const cycleBudgetField = this.getCycleBudgetField(amountCode);
        if (!cycleBudgetField) {
            return;
        }

        const selectedOption = PercentageCalculationRuleOptions.find(option => option.text === value);
        if (selectedOption) {
            cycleBudgetField.percentageCalculationRule = selectedOption.value;
        }
    }

    doesBudgetForAmountCodeExist(amountCode: AmountCode): boolean {
        const matchingAmount = this._cycleBudgetFieldsMap.get(amountCode);
        return matchingAmount ? this._cycleBudgets.find(cyclebudget =>
            matchingAmount.budgetCycleId === cyclebudget.budgetCycleId) !== undefined
            : false;
    }

    @computed
    get cycleBudgetFields(): CycleBudgetField[] {
        return Array.from(this._cycleBudgetFieldsMap.values());
    }

    @computed
    get possibleBudgetFieldsForTccCalc(): string[] {
        const budgetFields = this.cycleBudgetFields
            .filter(field => field.amountCode !== AmountCode.Stock)
            .map(field => field.title);
        budgetFields.push(EmployeePayTypeTitle.LastPaidSti);
        budgetFields.push(EmployeePayTypeTitle.Commissions);
        budgetFields.push(EmployeePayTypeTitle.OtherCash);
        return budgetFields;
    }

    @computed
    get allFieldNamesThatAffectTcc(): string[] {
        const budgetFields = this.cycleBudgetFields
            .filter(field => !this._teamPageViews.allExcludedFields.has(field.amountCode))
            .map(field => field.title);
        return [...budgetFields, ...this._teamPageViews.allNonBudgetFieldNamesThatAffectTcc];
    }

    hasCycleBudget(employeeAmount: IAmount): boolean {
        return !!this._cycleBudgets.find(budget =>
            budget.budgetCycleId === employeeAmount.budgetCycleId);
    }

    @computed
    get compSummaryFieldOptions(): string[] {
        const compSummaryFieldsOptions = this.cycleBudgetFields.map(field => field.title);
        compSummaryFieldsOptions.push(...GetAllEmployeePayTypeTitles());
        return compSummaryFieldsOptions;
    }

    @computed
    get selectedCompSummaryFields(): string[] {
        const selectedFields = this.cycleBudgetFields
            .filter(field => this.teamPageViews.isAmountUsedInCompSummary(field.amountCode))
            .map(field => field.title);

        if (this.teamPageViews.isLastPaidStiUsedInCompSummary) {
            selectedFields.push(EmployeePayTypeTitle.LastPaidSti);
        }

        if (this.teamPageViews.isOtherCashUsedInCompSummary) {
            selectedFields.push(EmployeePayTypeTitle.OtherCash);
        }

        if (this.teamPageViews.isCommissionsUsedInCompSummary) {
            selectedFields.push(EmployeePayTypeTitle.Commissions);
        }

        return selectedFields;
    }

    calculateExcludedCompSummaryFields(fieldsToInclude: string[]): string[] {
        const excludedFields = this.cycleBudgetFields
            .filter(field => fieldsToInclude.indexOf(field.title) === -1)
            .map(field => field.amountCode) as string[];

        if (fieldsToInclude.indexOf(EmployeePayTypeTitle.LastPaidSti) === -1) {
            excludedFields.push(EmployeePayType.LastPaidSti);
        }

        if (fieldsToInclude.indexOf(EmployeePayTypeTitle.OtherCash) === -1) {
            excludedFields.push(EmployeePayType.OtherCash);
        }

        if (fieldsToInclude.indexOf(EmployeePayTypeTitle.Commissions) === -1) {
            excludedFields.push(EmployeePayType.Commissions);
        }

        return excludedFields;
    }

    @action
    setShowPerformanceRating(value: boolean): void {
        if (!this._ratingConfiguration) {
            return;
        }
        this._ratingConfiguration.setShowPerformanceRating(value);
    }

    @action
    setEditPerformanceRating(value: boolean): void {
        if (!this._ratingConfiguration) {
            return;
        }
        this._ratingConfiguration.setAllowEditPerformanceRating(value);
    }

    @action
    setShowPayRange(value: boolean): void {
        this._payRangeConfiguration.setShowPayRange(value);
    }

    @action
    setPayRangeDisplayType(value: PayRangeDisplayType): void {
        this._payRangeConfiguration.setDisplayType(value);
    }

    @action
    setCompRatioDisplayFormat(value: CompaRatioDisplayFormat): void {
        this._payRangeConfiguration.setCompRatioDisplayFormat(value);
    }

    @action
    setBaseOfCalculation(value: BaseOfCalculation): void {
        this._payRangeConfiguration.setBaseOfCalculation(value);
    }

    @action
    setTccDisplayValue(value: boolean): void {
        this._teamPageViews.setTcc(value);
    }

    @action
    addRemoveFieldsForTccCalc(value: string[], remove: boolean): void {
        if (remove) {
            const check = new Set(value);
            const remaining = this.allFieldNamesThatAffectTcc.filter(fieldName =>
                !check.has(fieldName));
            this._teamPageViews.addFieldToExclude(this._tccAmountConverter[remaining[0]]);
        } else {
            const check = new Set(this._teamPageViews.allExcludedFields);
            const remaining = value.filter(fieldName =>
                check.has(this._tccAmountConverter[fieldName]));
            this._teamPageViews.removeFieldToExclude(this._tccAmountConverter[remaining[0]]);
        }
    }

    @action
    setUseCompSummaryFields(value: boolean): void {
        this._teamPageViews.setUseCompSummaryFields(value);
        if (!value) {
            this._teamPageViews.setExcludedCompSummaryFields([]);
        }
    }

    @action
    setHideIneligibleEmployees(value: boolean): void {
        this._teamPageViews.setHideIneligibleEmployees(value);
    }

    @action
    setDisableCompMemoDownload(value: boolean): void {
        this._teamPageViews.setDisableCompMemoDownload(value);
    }

    @action
    setExcludedCompSummaryFields(fields: string[]): void {
        this._teamPageViews.setExcludedCompSummaryFields(fields);
    }

    @action
    setShowIncreaseRecommendation(value: boolean): void {
        this._teamPageViews.setShowIncreaseRecommendation(value);
    }

    @action
    setManagerIncreaseDecision(value: boolean): void {
        this._teamPageViews.setManagerIncreaseDecision(value);
    }

    @action
    setManagerIncreasePercentageUnder(value: number): void {
        this._teamPageViews.setManagerIncreasePercentageUnder(value);
    }

    @action
    setManagerIncreasePercentageOver(value: number): void {
        this._teamPageViews.setManagerIncreasePercentageOver(value);
    }

    @action
    setPreFillDecision(value: boolean): void {
        this._teamPageViews.setPreFillDecision(value);
        if (!value) {
            this._teamPageViews.setPreFillDecisionFields([]);
        }
    }

    @action
    setPreFillDecisionFields(values: string[]): void {
        const preFillFields = this.cycleBudgetFields
            .filter(field => !!values.find(value => value === field.title))
            .map(field => field.amountCode.toString());
        this._teamPageViews.setPreFillDecisionFields(preFillFields);
    }

    @action
    toggleLockViewForManagers(value: boolean): void {
        this._teamPageViews.setLockViewForManager(value);
    }
}